import React, { useEffect, useState } from 'react';
import { timeAgo, fmtDate, fmtDateTime, parseUTC } from '../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { logApi } from '../api';
import { Spinner, EmptyState } from '../components/ui';

const CHANGE_ICONS = {
  CREATED:             '✨',
  STATUS_CHANGED:      '🔄',
  PRIORITY_CHANGED:    '⬆️',
  DEADLINE_CHANGED:    '📅',
  REASSIGNED:          '👤',
  DELETED:             '🗑️',
  RESTORED:            '♻️',
  NOTE_ADDED:          '💬',
  EXTENSION_REQUESTED: '📋',
  EXTENSION_APPROVED:  '✅',
  EXTENSION_REJECTED:  '❌',
  DUPLICATED:          '📑',
  POOL_CLAIMED:        '⊕',
  COMPLETION_REOPENED: '↩️',
  PASSWORD_RESET:      '🔑',
  EMPLOYEE_DEACTIVATED:'🚫',
  EMPLOYEE_ACTIVATED:  '✅',
};

function groupByDate(logs) {
  const groups = {};
  for (const l of logs) {
    const dateKey = fmtDate(l.timestamp);
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(l);
  }
  return groups;
}

export default function ActivityLog() {
  const navigate           = useNavigate();
  const [logs,    setLogs]   = useState([]);
  const [loading, setLoading]= useState(true);
  const [search,  setSearch] = useState('');
  const [typeFilter, setType]= useState('ALL');

  useEffect(() => {
    logApi.getActivity()
      .then(r => setLogs(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const TYPES = ['ALL', 'CREATED', 'STATUS_CHANGED', 'DEADLINE_CHANGED', 'REASSIGNED', 'DELETED', 'NOTE_ADDED'];

  const filtered = logs.filter(l => {
    const matchSearch = !search ||
      l.changedBy?.name?.toLowerCase().includes(search.toLowerCase()) ||
      l.changeType?.toLowerCase().includes(search.toLowerCase()) ||
      l.newValue?.toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === 'ALL' || l.changeType === typeFilter;
    return matchSearch && matchType;
  });

  const grouped = groupByDate(filtered);

  return (
    <div style={{ padding: '24px', maxWidth: '800px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={S.title}>Activity Log</h1>
        <p style={S.sub}>{logs.length} total entries — append-only, cannot be edited</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
        <input
          placeholder="Search by person, action, or value..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={S.input}
        />
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {TYPES.map(t => (
            <button key={t} onClick={() => setType(t)} style={{
              padding: '4px 10px', borderRadius: '999px', cursor: 'pointer', fontSize: '11px', fontWeight: typeFilter === t ? 600 : 400,
              background: typeFilter === t ? 'var(--color-primary)' : 'var(--color-surface)',
              color:      typeFilter === t ? '#fff' : 'var(--color-text-secondary)',
              border:     `1px solid ${typeFilter === t ? 'var(--color-primary)' : 'var(--color-border)'}`,
            }}>{t === 'ALL' ? 'All' : t.replace(/_/g, ' ')}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Spinner /></div>
      ) : filtered.length === 0 ? (
        <EmptyState icon="📋" title="No activity yet" subtitle="Every task change will be recorded here." />
      ) : (
        Object.entries(grouped).map(([date, entries]) => (
          <div key={date} style={{ marginBottom: '24px' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-muted)', marginBottom: '8px' }}>
              {date}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {entries.map(log => (
                <div key={log.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px',
                  padding: '10px 14px', background: 'var(--color-surface)',
                  borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)',
                }}>
                  <span style={{ fontSize: '16px', flexShrink: 0, marginTop: '1px' }}>
                    {CHANGE_ICONS[log.changeType] || '•'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: '13px', color: 'var(--color-text)', lineHeight: 1.5 }}>
                      <strong>{log.changedBy?.name || 'System'}</strong>
                      {' '}
                      <span style={{ color: 'var(--color-primary)' }}>
                        {log.changeType.replace(/_/g, ' ').toLowerCase()}
                      </span>
                      {log.oldValue && log.newValue && (
                        <span style={{ color: 'var(--color-text-muted)' }}>
                          {' · '}{log.oldValue} → {log.newValue}
                        </span>
                      )}
                      {!log.oldValue && log.newValue && (
                        <span style={{ color: 'var(--color-text-muted)' }}> · {log.newValue}</span>
                      )}
                      {log.note && (
                        <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}> · {log.note}</span>
                      )}
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    {log.taskId && (
                      <button onClick={() => navigate(`/tasks/${log.taskId}`)} style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: '11px', color: 'var(--color-accent)', padding: 0,
                      }}>View →</button>
                    )}
                    <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>{timeAgo(log.timestamp)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

const S = {
  title: { fontSize: '22px', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '2px' },
  sub:   { fontSize: '13px', color: 'var(--color-text-muted)' },
  input: { padding: '10px 14px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--color-border)', fontSize: '14px', width: '100%' },
};
